// Nova Lance sound candidates A / B / C (owner 2026-11-02: "I want to change the sound of the Nova Lance"). Original
// procedural WebAudio synthesis of the genre's big two-hand charged energy beam, in four phases:
//   charge  — power gathering in cupped hands: a rising hum with tension, a shimmering top, a low pulse that speeds up and
//             tiny electric crackles, ending in a short pitch pinch exactly on release;
//   release — an explosive burst: a big low thump, a bright transient, a wide stereo bloom and air rushing out;
//   sustain — a thick roaring torrent (detuned saw stack + filtered noise with flutter / tremolo, a deep body, a sizzling
//             top, crackle) that holds for exactly as long as the beam: it has no fixed length and no sample loop, so it
//             has no seam; sync() moves its end to the sim's real beam end (game speed, hit-stop);
//   tail    — the stream thins (a rising high-pass while it fades) and dissipates with a few last crackles.
// Picked with ?lancesfx=a|b|c (src/main.ts). The default stays 'old' (the current sample-based sound in sfx.ts) until the
// owner chooses. Rendered + measured headlessly by scripts/lance-sfx-check.mjs (src/audio/lanceSfxQa.ts); the node graph
// is unit-tested against a fake AudioContext in tests/lanceSfx.test.ts.
// CPU: one voice is ~80 nodes / ~22 sources; at most MAX_VOICES beams sound at once (the oldest is cut), noise comes from
// the shared seamless loops in kit.ts, crackle is ONE noise source per phase gated by automation (no node per grain), and
// every node of a voice is disconnected once its last source has ended.
import type { NoiseColor } from './mapAudioDef';
import { noiseBuffer } from './kit';
import { mulberry32 } from './noise';

export type LanceDesign = 'old' | 'a' | 'b' | 'c';
export const LANCE_CANDIDATES = ['a', 'b', 'c'] as const;
/** URL value -> design ('old' = the current sound, also for anything unknown) */
export const lanceDesignOf = (v: string | null | undefined): LanceDesign => (v === 'a' || v === 'b' || v === 'c' ? v : 'old');
/** at most this many beam voices at once (Twin-Star's short cooldown, a re-fire during a long tail); the oldest is cut */
export const MAX_VOICES = 3;

type Osc = [mult: number, cents: number, type: OscillatorType, level: number];
type SawVoice = [mult: number, cents: number, pan: number];
interface Recipe {
  /** overall trims (level-matched to the current lance in lanceSfxQa) */
  chargeOut: number; fireOut: number;
  charge: {
    hum: { f0: number; f1: number; pinch: number; voices: Osc[]; lp0: number; lp1: number; q: number; level: number };
    /** sub that pulses (AM) faster and faster: r0 -> r1 Hz */
    pulse: { f0: number; f1: number; r0: number; r1: number; depth: number; level: number };
    rumble: { lp0: number; lp1: number; level: number } | null;
    shimmer: { f0: number; f1: number; ratios: number[]; vib: number; cents: number; level: number; from: number };
    rush: { color: NoiseColor; f0: number; f1: number; q: number; level: number };
    crackle: { d0: number; d1: number; level: number; fLo: number; fHi: number; from: number; decay: number };
    wet: number;
  };
  release: {
    thump: { f0: number; f1: number; dur: number; level: number };
    punch: { f0: number; f1: number; dur: number; drive: number; level: number };
    crack: { hp: number; dur: number; level: number };
    zap: { f0: number; f1: number; dur: number; level: number } | null;
    bloom: { fL: number; fR: number; q: number; dur: number; level: number; haas: number };
    air: { f0: number; f1: number; dur: number; level: number };
    wet: number;
  };
  sustain: {
    saw: { f: number; voices: SawVoice[]; type: OscillatorType; drive: number; lp: number; q: number; flutterHz: number; flutterDepth: number; level: number; glide: number; glideT: number };
    roar: { color: NoiseColor; f: number; q: number; level: number; tremHz: number; tremDepth: number; swellHz: number; swellDepth: number };
    body: { f: number; level: number };
    sizzle: { hp: number; level: number; amHz: number; amDepth: number };
    /** a cleaner pitched core (C only) */
    core: { f: number; cents: number; vibHz: number; vibCents: number; bp: number; q: number; level: number } | null;
    crackle: { rate: number; level: number; fLo: number; fHi: number; decay: number };
    attack: number; wet: number;
  };
  tail: { fade: number; thinTo: number; dur: number; whisp: { f0: number; f1: number; q: number; level: number }; sigh: { lp: number; level: number }; crackles: number; crackleLevel: number };
}

/** A — "Classic surge": closest to the genre convention. Octave-rising cupped-hands hum, a sub pulse that accelerates,
 *  a glassy shimmer; a round thump + bright crack; a mid-weight roaring stream with a fluttering filter. */
const A: Recipe = {
  chargeOut: 3.6, fireOut: 0.7,
  charge: {
    hum: { f0: 98, f1: 196, pinch: 1.12, voices: [[1, 0, 'sawtooth', 0.5], [1, 9, 'sawtooth', 0.4], [2, -6, 'triangle', 0.5], [1.5, 4, 'triangle', 0.3]], lp0: 500, lp1: 3800, q: 3, level: 0.16 },
    pulse: { f0: 45, f1: 62, r0: 4, r1: 20, depth: 0.45, level: 0.24 },
    rumble: null,
    shimmer: { f0: 1760, f1: 2960, ratios: [1, 1.5, 2.01], vib: 8, cents: 25, level: 0.03, from: 0.35 },
    rush: { color: 'white', f0: 300, f1: 4200, q: 1.8, level: 0.22 },
    crackle: { d0: 6, d1: 40, level: 0.12, fLo: 2500, fHi: 8000, from: 0.2, decay: 0.012 },
    wet: 0.5,
  },
  release: {
    thump: { f0: 120, f1: 36, dur: 0.5, level: 0.7 },
    punch: { f0: 220, f1: 70, dur: 0.22, drive: 4, level: 0.35 },
    crack: { hp: 1800, dur: 0.09, level: 0.6 },
    zap: null,
    bloom: { fL: 900, fR: 1300, q: 0.7, dur: 0.55, level: 0.7, haas: 0.014 },
    air: { f0: 5500, f1: 500, dur: 0.7, level: 0.35 },
    wet: 0.6,
  },
  sustain: {
    saw: { f: 82.4, voices: [[1, 0, 0], [1, -11, -0.6], [1, 12, 0.6], [2, -5, -0.3], [2, 6, 0.3], [1.5, 3, 0]], type: 'sawtooth', drive: 2.2, lp: 2400, q: 1.4, flutterHz: 9, flutterDepth: 450, level: 0.22, glide: 1.12, glideT: 0.18 },
    roar: { color: 'pink', f: 900, q: 0.7, level: 0.8, tremHz: 8, tremDepth: 0.25, swellHz: 0.6, swellDepth: 0.12 },
    body: { f: 41, level: 0.08 },
    sizzle: { hp: 5200, level: 0.16, amHz: 21, amDepth: 0.5 },
    core: null,
    crackle: { rate: 26, level: 0.25, fLo: 3000, fHi: 9000, decay: 0.01 },
    attack: 0.07, wet: 0.3,
  },
  tail: { fade: 0.3, thinTo: 1400, dur: 1.0, whisp: { f0: 2600, f1: 6500, q: 2, level: 0.18 }, sigh: { lp: 400, level: 0.12 }, crackles: 6, crackleLevel: 0.2 },
};

/** B — "Heavier": an octave lower and more cinematic. A slow, deep throb that races at the end over a brown-noise rumble;
 *  a long boom with a saturated mid punch and a big room; a growling low stream with a slow, heavy tremolo; a long tail. */
const B: Recipe = {
  chargeOut: 2.5, fireOut: 1.0,
  charge: {
    hum: { f0: 55, f1: 110, pinch: 1.08, voices: [[1, 0, 'sawtooth', 0.5], [1, 14, 'sawtooth', 0.45], [1, -13, 'sawtooth', 0.45], [2, 5, 'triangle', 0.4]], lp0: 260, lp1: 1800, q: 5, level: 0.22 },
    pulse: { f0: 32, f1: 48, r0: 2.5, r1: 14, depth: 0.6, level: 0.35 },
    rumble: { lp0: 180, lp1: 420, level: 0.12 },
    shimmer: { f0: 1200, f1: 1800, ratios: [1, 1.498], vib: 5, cents: 20, level: 0.015, from: 0.55 },
    rush: { color: 'pink', f0: 180, f1: 2200, q: 1.2, level: 0.25 },
    crackle: { d0: 3, d1: 22, level: 0.09, fLo: 1500, fHi: 5000, from: 0.35, decay: 0.016 },
    wet: 0.6,
  },
  release: {
    thump: { f0: 85, f1: 26, dur: 0.9, level: 0.85 },
    punch: { f0: 160, f1: 48, dur: 0.35, drive: 6, level: 0.45 },
    crack: { hp: 1200, dur: 0.12, level: 0.5 },
    zap: null,
    bloom: { fL: 500, fR: 750, q: 0.6, dur: 0.9, level: 0.55, haas: 0.02 },
    air: { f0: 3500, f1: 250, dur: 1.0, level: 0.35 },
    wet: 0.85,
  },
  sustain: {
    saw: { f: 55, voices: [[1, 0, 0], [1, -14, -0.7], [1, 15, 0.7], [1.5, -6, -0.4], [1.5, 7, 0.4], [0.5, 0, 0]], type: 'sawtooth', drive: 3.5, lp: 1300, q: 2.2, flutterHz: 5.5, flutterDepth: 260, level: 0.26, glide: 1.2, glideT: 0.3 },
    roar: { color: 'brown', f: 520, q: 0.6, level: 0.35, tremHz: 5, tremDepth: 0.3, swellHz: 0.35, swellDepth: 0.15 },
    body: { f: 34, level: 0.12 },
    sizzle: { hp: 4200, level: 0.1, amHz: 13, amDepth: 0.4 },
    core: null,
    crackle: { rate: 12, level: 0.15, fLo: 1800, fHi: 6000, decay: 0.014 },
    attack: 0.12, wet: 0.4,
  },
  tail: { fade: 0.5, thinTo: 700, dur: 1.6, whisp: { f0: 1200, f1: 3500, q: 1.4, level: 0.15 }, sigh: { lp: 260, level: 0.2 }, crackles: 4, crackleLevel: 0.14 },
};

/** C — "Brighter / sharper": a steep 1.6-octave climb with a bright glassy shimmer and dense crackle; a snappy crack + zap;
 *  a clean pitched laser core over a lighter, fast-fluttering stream with a strong sizzle; a short sparkling tail. */
const C: Recipe = {
  chargeOut: 4.2, fireOut: 1.35,
  charge: {
    hum: { f0: 196, f1: 587, pinch: 1.2, voices: [[1, 0, 'square', 0.3], [1, 7, 'sawtooth', 0.35], [2, -4, 'triangle', 0.4], [3, 3, 'sine', 0.25]], lp0: 1200, lp1: 7000, q: 4, level: 0.11 },
    pulse: { f0: 55, f1: 82, r0: 6, r1: 30, depth: 0.4, level: 0.12 },
    rumble: null,
    shimmer: { f0: 2637, f1: 5274, ratios: [1, 1.26, 1.5, 2], vib: 11, cents: 35, level: 0.035, from: 0.2 },
    rush: { color: 'white', f0: 900, f1: 8000, q: 2.5, level: 0.18 },
    crackle: { d0: 12, d1: 90, level: 0.1, fLo: 3500, fHi: 11000, from: 0.1, decay: 0.01 },
    wet: 0.45,
  },
  release: {
    thump: { f0: 170, f1: 55, dur: 0.3, level: 0.45 },
    punch: { f0: 400, f1: 120, dur: 0.12, drive: 3, level: 0.25 },
    crack: { hp: 3000, dur: 0.07, level: 0.55 },
    zap: { f0: 4200, f1: 380, dur: 0.12, level: 0.25 },
    bloom: { fL: 2200, fR: 3100, q: 0.9, dur: 0.4, level: 0.45, haas: 0.01 },
    air: { f0: 9000, f1: 1500, dur: 0.5, level: 0.3 },
    wet: 0.5,
  },
  sustain: {
    saw: { f: 110, voices: [[1, 0, 0], [1, -8, -0.5], [1, 8, 0.5]], type: 'sawtooth', drive: 1.6, lp: 2600, q: 1, flutterHz: 16, flutterDepth: 500, level: 0.12, glide: 1.06, glideT: 0.1 },
    roar: { color: 'white', f: 2200, q: 0.9, level: 0.28, tremHz: 15, tremDepth: 0.2, swellHz: 0.9, swellDepth: 0.1 },
    body: { f: 55, level: 0.05 },
    sizzle: { hp: 6500, level: 0.2, amHz: 31, amDepth: 0.6 },
    core: { f: 440, cents: 6, vibHz: 17, vibCents: 18, bp: 1800, q: 1.6, level: 0.14 },
    crackle: { rate: 55, level: 0.24, fLo: 3500, fHi: 12000, decay: 0.007 },
    attack: 0.04, wet: 0.25,
  },
  tail: { fade: 0.22, thinTo: 2400, dur: 0.8, whisp: { f0: 4500, f1: 9000, q: 3, level: 0.18 }, sigh: { lp: 900, level: 0.06 }, crackles: 10, crackleLevel: 0.25 },
};

export const LANCE_RECIPES: Record<Exclude<LanceDesign, 'old'>, Recipe> = { a: A, b: B, c: C };

/** the shared noise loops are RMS 0.25 (kit.ts): noise-layer levels in the recipes are relative to unit-RMS noise */
const NZ = 4;
/** crackle grains go through a narrow band-pass (Q 5): a further make-up so `level` is roughly the grain's amplitude */
const CRK = NZ * 2.5;
const shaperCurves = new Map<number, Float32Array<ArrayBuffer>>();
const exp = (p: AudioParam, v: number, t: number) => p.exponentialRampToValueAtTime(Math.max(1e-4, v), t);

/** Every node a phase creates goes in a bag, so the whole voice can be disconnected in one go. */
class Bag {
  readonly nodes: AudioNode[] = [];
  readonly srcs: AudioScheduledSourceNode[] = [];
  /** sources whose scheduled start was cancelled (a re-planned tail): never stopped again, or a later stop() would let them play */
  readonly dropped = new Set<AudioScheduledSourceNode>();
  constructor(readonly c: BaseAudioContext, readonly rand: () => number) {}
  private own<T extends AudioNode>(n: T): T { this.nodes.push(n); return n; }
  gain(v: number): GainNode { const g = this.own(this.c.createGain()); g.gain.value = v; return g; }
  filter(type: BiquadFilterType, f: number, q = 0.707): BiquadFilterNode { const b = this.own(this.c.createBiquadFilter()); b.type = type; b.frequency.value = f; b.Q.value = q; return b; }
  pan(p: number): StereoPannerNode { const s = this.own(this.c.createStereoPanner()); s.pan.value = p; return s; }
  delay(s: number): DelayNode { const d = this.own(this.c.createDelay(0.1)); d.delayTime.value = s; return d; }
  shaper(amount: number): WaveShaperNode {
    const ws = this.own(this.c.createWaveShaper());
    let curve = shaperCurves.get(amount);
    if (!curve) { const n = 1024; curve = new Float32Array(n); for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; curve[i] = Math.tanh(x * amount) / Math.tanh(amount); } shaperCurves.set(amount, curve); }
    ws.curve = curve; ws.oversample = '2x'; return ws;
  }
  osc(type: OscillatorType, f: number, t0: number, cents = 0): OscillatorNode {
    const o = this.own(this.c.createOscillator()); o.type = type; o.frequency.value = f; o.detune.value = cents; o.start(t0); this.srcs.push(o); return o;
  }
  /** looping shared noise (kit.ts: seamless, stereo) at a random offset so layers never phase */
  noise(color: NoiseColor, t0: number): AudioBufferSourceNode {
    const s = this.own(this.c.createBufferSource()); s.buffer = noiseBuffer(this.c, color); s.loop = true;
    s.start(t0, this.rand() * s.buffer.duration); this.srcs.push(s); return s;
  }
  /** an LFO of `depth` added onto `param` */
  lfo(param: AudioParam, hz: number, depth: number, t0: number, type: OscillatorType = 'sine'): OscillatorNode {
    const o = this.osc(type, hz, t0), g = this.gain(depth); o.connect(g).connect(param); return o;
  }
  stop(at: number, from = 0, to = this.srcs.length): void { for (let i = from; i < to; i++) { const s = this.srcs[i]; if (this.dropped.has(s)) continue; try { s.stop(at); } catch { /* already */ } } }
  drop(from: number): void { for (let i = from; i < this.srcs.length; i++) { const s = this.srcs[i]; if (this.dropped.has(s)) continue; s.onended = null; try { s.stop(this.c.currentTime); } catch { /* already */ } this.dropped.add(s); } }
  free(): void { for (const n of this.nodes) { try { n.disconnect(); } catch { /* already */ } } this.nodes.length = 0; this.srcs.length = 0; this.dropped.clear(); }
}

/** Crackle: ONE noise source through a band-pass and a gate; each grain is a few automation events (frequency and pan hop
 *  while the gate is closed), so a dense crackle costs no extra nodes. Grains never overlap (gap >= 2.6 decays), so a new
 *  grain never cuts a ringing one off. */
class Crackle {
  private bp: BiquadFilterNode; private g: GainNode; private p: StereoPannerNode;
  until: number;
  constructor(private b: Bag, dest: AudioNode, t0: number, private fLo: number, private fHi: number, private decay: number) {
    const src = b.noise('white', t0); this.bp = b.filter('bandpass', (fLo + fHi) / 2, 5); this.g = b.gain(0); this.p = b.pan(0);
    src.connect(this.bp).connect(this.g).connect(this.p).connect(dest); this.until = t0;
  }
  /** grains from t0 to t1, density r0 -> r1 per second (bounded: at most 400 per call) */
  grains(t0: number, t1: number, r0: number, r1: number, level: number): void {
    const { rand } = this.b, minGap = this.decay * 2.6;
    let tt = Math.max(t0, this.until, this.b.c.currentTime);
    for (let n = 0; n < 400 && tt < t1; n++) {
      const u = (tt - t0) / Math.max(1e-3, t1 - t0), rate = r0 + (r1 - r0) * u, a = Math.max(0.0012, this.decay * 0.25);
      this.bp.frequency.setValueAtTime(this.fLo + rand() * (this.fHi - this.fLo), tt);
      this.p.pan.setValueAtTime(rand() * 1.6 - 0.8, tt);
      this.g.gain.setValueAtTime(0, tt); this.g.gain.linearRampToValueAtTime(CRK * level * (0.35 + 0.65 * rand()), tt + a);
      this.g.gain.setTargetAtTime(0, tt + a, this.decay / 3);
      tt += Math.max(minGap, (0.4 + 1.2 * rand()) / Math.max(0.5, rate));
    }
    this.until = Math.max(this.until, tt);
  }
}

interface Voice { b: Bag; out: GainNode; hold: GainNode; thin: BiquadFilterNode; crackle: Crackle; r: Recipe; k: number; susFrom: number; susTo: number; tailFrom: number; endAt: number; moves: number; dead: boolean }
interface ChargeVoice { b: Bag; hold: GainNode; end: number; dead: boolean }

export class LanceSynth {
  private voices: Voice[] = [];
  private charging: ChargeVoice | null = null;
  private seed = 1;
  constructor(private c: BaseAudioContext, private dest: AudioNode, private send: AudioNode) {}
  private rand(): () => number { return mulberry32(0x1a2b + this.seed++ * 7919); }

  /** live beam voices (QA) */
  get active(): number { return this.voices.length; }
  stats(): { voices: number; nodes: number; sources: number; charging: boolean } {
    let nodes = this.charging?.b.nodes.length ?? 0, sources = this.charging?.b.srcs.length ?? 0;
    for (const v of this.voices) { nodes += v.b.nodes.length; sources += v.b.srcs.length; }
    return { voices: this.voices.length, nodes, sources, charging: !!this.charging };
  }

  /** The charge, `dur` real seconds long; its pinch lands on the release. */
  charge(design: Exclude<LanceDesign, 'old'>, dur: number, gold = false): void {
    const R = LANCE_RECIPES[design], P = R.charge, c = this.c, t = c.currentTime, D = Math.max(0.2, dur), E = t + D, k = gold ? 1.06 : 1;
    this.cutCharge(t);
    const b = new Bag(c, this.rand()), out = b.gain(R.chargeOut), hold = b.gain(1), wet = b.gain(P.wet);
    hold.connect(out); out.connect(this.dest); out.connect(wet).connect(this.send);
    // shared accelerating pulse LFO (sub AM + a little on the hum)
    const lfo = b.osc('sine', P.pulse.r0, t); exp(lfo.frequency, P.pulse.r0, t); exp(lfo.frequency, P.pulse.r1, E);
    // 1) hum: detuned voices rising f0 -> f1, a last pinch up, through a low-pass that opens; envelope swells
    {
      const lp = b.filter('lowpass', P.hum.lp0, P.hum.q), env = b.gain(0), am = b.gain(0.85), d = b.gain(0.15);
      lp.frequency.setValueAtTime(P.hum.lp0, t); exp(lp.frequency, P.hum.lp1 * k, E);
      lfo.connect(d).connect(am.gain);
      env.gain.setValueAtTime(1e-4, t); exp(env.gain, P.hum.level * 0.25, t + D * 0.3); exp(env.gain, P.hum.level, E - 0.02); exp(env.gain, 1e-4, E + 0.05);
      for (const [m, cents, type, lv] of P.hum.voices) {
        const o = b.osc(type, P.hum.f0 * k * m, t, cents), g = b.gain(lv), f0 = P.hum.f0 * k * m, f1 = P.hum.f1 * k * m;
        o.frequency.setValueAtTime(f0, t); exp(o.frequency, f1, E - 0.08); exp(o.frequency, f1 * P.hum.pinch, E);
        o.connect(g).connect(lp);
      }
      lp.connect(am).connect(env).connect(hold);
    }
    // 2) low pulse: sub sine, amplitude-modulated by the accelerating LFO, deepening toward release
    {
      const o = b.osc('sine', P.pulse.f0 * k, t), am = b.gain(1 - P.pulse.depth), d = b.gain(0), env = b.gain(0);
      o.frequency.setValueAtTime(P.pulse.f0 * k, t); exp(o.frequency, P.pulse.f1 * k, E);
      d.gain.setValueAtTime(P.pulse.depth * 0.4, t); d.gain.linearRampToValueAtTime(P.pulse.depth, E);
      lfo.connect(d).connect(am.gain);
      env.gain.setValueAtTime(1e-4, t); exp(env.gain, P.pulse.level * 0.3, t + D * 0.4); exp(env.gain, P.pulse.level, E - 0.03); exp(env.gain, 1e-4, E + 0.06);
      o.connect(am).connect(env).connect(hold);
    }
    // 2b) rumble bed (B): brown noise low-passed, riding the same pulse
    if (P.rumble) {
      const n = b.noise('brown', t), lp = b.filter('lowpass', P.rumble.lp0, 1.2), am = b.gain(0.6), d = b.gain(0.4), env = b.gain(0);
      lp.frequency.setValueAtTime(P.rumble.lp0, t); exp(lp.frequency, P.rumble.lp1, E);
      lfo.connect(d).connect(am.gain);
      env.gain.setValueAtTime(1e-4, t); exp(env.gain, NZ * P.rumble.level * 0.4, t + D * 0.5); exp(env.gain, NZ * P.rumble.level, E - 0.03); exp(env.gain, 1e-4, E + 0.06);
      n.connect(lp).connect(am).connect(env).connect(hold);
    }
    // 3) shimmer: glassy partials with vibrato, rising, faded in over the later part
    {
      const env = b.gain(0), vib = b.osc('sine', P.shimmer.vib, t), vg = b.gain(P.shimmer.cents);
      vib.connect(vg);
      env.gain.setValueAtTime(1e-4, t); env.gain.setValueAtTime(1e-4, t + D * P.shimmer.from); exp(env.gain, P.shimmer.level, E - 0.02); exp(env.gain, 1e-4, E + 0.04);
      P.shimmer.ratios.forEach((r, i) => {
        const o = b.osc('sine', P.shimmer.f0 * k * r, t, (i - 1) * 4), p = b.pan(((i % 2) * 2 - 1) * 0.45);
        o.frequency.setValueAtTime(P.shimmer.f0 * k * r, t); exp(o.frequency, P.shimmer.f1 * k * r, E);
        vg.connect(o.detune); o.connect(p).connect(env);
      });
      env.connect(hold);
    }
    // 4) inward rush: noise through a band-pass sweeping up, swelling to the release
    {
      const n = b.noise(P.rush.color, t), f = b.filter('bandpass', P.rush.f0, P.rush.q), env = b.gain(0);
      f.frequency.setValueAtTime(P.rush.f0, t); exp(f.frequency, P.rush.f1 * k, E);
      env.gain.setValueAtTime(1e-4, t); exp(env.gain, NZ * P.rush.level * 0.12, t + D * 0.5); exp(env.gain, NZ * P.rush.level, E - 0.015); exp(env.gain, 1e-4, E + 0.03);
      n.connect(f).connect(env).connect(hold);
    }
    // 5) crackle: sparse, then dense near release
    new Crackle(b, hold, t, P.crackle.fLo, P.crackle.fHi, P.crackle.decay).grains(t + D * P.crackle.from, E - 0.02, P.crackle.d0, P.crackle.d1, P.crackle.level);
    b.stop(E + 0.1);
    const cv: ChargeVoice = { b, hold, end: E, dead: false };
    b.srcs[0].onended = () => { cv.dead = true; b.free(); if (this.charging === cv) this.charging = null; };
    this.charging = cv;
  }

  /** Release + sustain + tail. `dur` = the beam's expected real length; sync() corrects it while the beam runs. */
  fire(design: Exclude<LanceDesign, 'old'>, dur: number, gold = false): void {
    const R = LANCE_RECIPES[design], c = this.c, t = c.currentTime, k = gold ? 1.05 : 1;
    this.cutCharge(t);
    while (this.voices.length >= MAX_VOICES) this.kill(this.voices[0], t);
    const b = new Bag(c, this.rand()), out = b.gain(R.fireOut);
    out.connect(this.dest);
    // ---- release burst (one-shots, straight to out) ----
    {
      const P = R.release, rel = b.gain(1), wet = b.gain(P.wet); rel.connect(out); rel.connect(wet).connect(this.send);
      const thump = b.osc('sine', P.thump.f0 * k, t), tg = b.gain(0);
      thump.frequency.setValueAtTime(P.thump.f0 * k, t); exp(thump.frequency, P.thump.f1, t + P.thump.dur);
      tg.gain.setValueAtTime(1e-4, t); exp(tg.gain, P.thump.level, t + 0.004); exp(tg.gain, 1e-4, t + P.thump.dur);
      thump.connect(tg).connect(rel);
      const punch = b.osc('triangle', P.punch.f0 * k, t), ws = b.shaper(P.punch.drive), pg = b.gain(0);
      punch.frequency.setValueAtTime(P.punch.f0 * k, t); exp(punch.frequency, P.punch.f1, t + P.punch.dur);
      pg.gain.setValueAtTime(1e-4, t); exp(pg.gain, P.punch.level, t + 0.003); exp(pg.gain, 1e-4, t + P.punch.dur);
      punch.connect(ws).connect(pg).connect(rel);
      const cr = b.noise('white', t), hp = b.filter('highpass', P.crack.hp, 0.7), cg = b.gain(0);
      cg.gain.setValueAtTime(1e-4, t); exp(cg.gain, NZ * P.crack.level, t + 0.0015); exp(cg.gain, 1e-4, t + P.crack.dur);
      cr.connect(hp).connect(cg).connect(rel);
      if (P.zap) {
        const z = b.osc('sine', P.zap.f0 * k, t), zg = b.gain(0);
        z.frequency.setValueAtTime(P.zap.f0 * k, t); exp(z.frequency, P.zap.f1, t + P.zap.dur);
        zg.gain.setValueAtTime(1e-4, t); exp(zg.gain, P.zap.level, t + 0.002); exp(zg.gain, 1e-4, t + P.zap.dur);
        z.connect(zg).connect(rel);
      }
      // stereo bloom: one pink burst split into two bands, hard-ish left / right, the right one Haas-delayed
      const bn = b.noise('pink', t), bg = b.gain(0), fl = b.filter('bandpass', P.bloom.fL, P.bloom.q), fr = b.filter('bandpass', P.bloom.fR, P.bloom.q);
      const pl = b.pan(-0.85), pr = b.pan(0.85), hd = b.delay(P.bloom.haas);
      bg.gain.setValueAtTime(1e-4, t); exp(bg.gain, NZ * P.bloom.level, t + 0.012); exp(bg.gain, NZ * P.bloom.level * 0.35, t + P.bloom.dur * 0.35); exp(bg.gain, 1e-4, t + P.bloom.dur);
      bn.connect(bg); bg.connect(fl).connect(pl).connect(rel); bg.connect(fr).connect(hd).connect(pr).connect(rel);
      // air rushing out: a band-passed whoosh sweeping down
      const an = b.noise('white', t), af = b.filter('bandpass', P.air.f0, 1.1), ag = b.gain(0);
      af.frequency.setValueAtTime(P.air.f0, t); exp(af.frequency, P.air.f1, t + P.air.dur);
      ag.gain.setValueAtTime(1e-4, t); exp(ag.gain, NZ * P.air.level, t + 0.05); exp(ag.gain, 1e-4, t + P.air.dur);
      an.connect(af).connect(ag).connect(rel);
      const relEnd = t + Math.max(P.thump.dur, P.punch.dur, P.crack.dur, P.zap?.dur ?? 0, P.bloom.dur, P.air.dur) + 0.03;
      b.stop(relEnd);
    }
    // ---- sustain: everything -> sus (attack) -> thin (high-pass, opens at the end) -> hold (release) -> out ----
    const S = R.sustain, t1 = t + 0.025, susFrom = b.srcs.length;
    const sus = b.gain(0), thin = b.filter('highpass', 20, 0.5), hold = b.gain(1), wet = b.gain(S.wet);
    sus.gain.setValueAtTime(1e-4, t1); exp(sus.gain, 1, t1 + S.attack);
    sus.connect(thin).connect(hold).connect(out); hold.connect(wet).connect(this.send);
    // detuned saw stack, spread across the field, soft-saturated, through a low-pass whose cutoff flutters
    {
      const P = S.saw, mix = b.gain(1 / Math.sqrt(P.voices.length)), ws = b.shaper(P.drive), lp = b.filter('lowpass', P.lp * k, P.q), g = b.gain(P.level);
      const pans = new Map<number, StereoPannerNode>();
      for (const [m, cents, pan] of P.voices) {
        const f = P.f * k * m, o = b.osc(P.type, f * P.glide, t1, cents);
        o.frequency.setValueAtTime(f * P.glide, t1); exp(o.frequency, f, t1 + P.glideT);
        let pn = pans.get(pan); if (!pn) { pn = b.pan(pan); pn.connect(mix); pans.set(pan, pn); }
        o.connect(pn);
      }
      b.lfo(lp.frequency, P.flutterHz, P.flutterDepth, t1);
      mix.connect(ws).connect(lp).connect(g).connect(sus);
    }
    // roaring noise torrent: band-passed noise with a flutter tremolo and a slow swell
    {
      const P = S.roar, n = b.noise(P.color, t1), f = b.filter('bandpass', P.f * k, P.q), am = b.gain(1 - P.tremDepth - P.swellDepth), g = b.gain(NZ * P.level);
      b.lfo(am.gain, P.tremHz, P.tremDepth, t1); b.lfo(am.gain, P.swellHz, P.swellDepth, t1);
      n.connect(f).connect(am).connect(g).connect(sus);
    }
    // deep body
    { const o = b.osc('sine', S.body.f * k, t1), g = b.gain(S.body.level); o.connect(g).connect(sus); }
    // sizzling top with a fast shimmer
    {
      const P = S.sizzle, n = b.noise('white', t1), hp = b.filter('highpass', P.hp, 0.7), am = b.gain(1 - P.amDepth), g = b.gain(NZ * P.level);
      b.lfo(am.gain, P.amHz, P.amDepth, t1);
      n.connect(hp).connect(am).connect(g).connect(sus);
    }
    // clean pitched core (C)
    if (S.core) {
      const P = S.core, f = b.filter('bandpass', P.bp * k, P.q), g = b.gain(P.level), vg = b.gain(P.vibCents), vib = b.osc('sine', P.vibHz, t1);
      vib.connect(vg);
      for (const [type, cents, lv] of [['square', -P.cents, 0.5], ['sawtooth', P.cents, 0.6], ['sine', 1200, 0.35]] as [OscillatorType, number, number][]) {
        const o = b.osc(type, P.f * k, t1, cents), og = b.gain(lv); vg.connect(o.detune); o.connect(og).connect(f);
      }
      f.connect(g).connect(sus);
    }
    const crackle = new Crackle(b, sus, t1, S.crackle.fLo, S.crackle.fHi, S.crackle.decay);
    const v: Voice = { b, out, hold, thin, crackle, r: R, k, susFrom, susTo: b.srcs.length, tailFrom: b.srcs.length, endAt: t + dur, moves: 0, dead: false };
    this.voices.push(v);
    this.applyEnd(v, t + Math.max(0.15, dur));
  }

  /** Per frame while a run is live: `remaining` = real seconds left on the beam (0 = it has ended). Moves the newest
   *  voice's end to match the sim (later on hit-stop / slow game speed, now if the beam stopped early). */
  sync(remaining: number): void {
    const v = this.voices[this.voices.length - 1], now = this.c.currentTime;
    if (!v || v.dead || now >= v.endAt - 0.02) return; // already releasing
    if (remaining <= 0) { if (v.endAt > now + 0.05) this.applyEnd(v, now); return; }
    const want = now + remaining;
    if (want > v.endAt + 0.08 && v.moves < 24) this.applyEnd(v, want);
  }

  /** (Re)schedule the end: hold the stream until E, then fade + thin it, crackle up to E, and a fresh dissipating tail. */
  private applyEnd(v: Voice, E: number): void {
    const c = this.c, now = c.currentTime, T = Math.max(E, now), R = v.r, P = R.tail, b = v.b;
    v.moves++;
    // drop a previously scheduled (not yet started) tail
    b.drop(v.tailFrom);
    v.hold.gain.cancelScheduledValues(now); v.hold.gain.setValueAtTime(1, now); v.hold.gain.setValueAtTime(1, T); v.hold.gain.setTargetAtTime(0, T, P.fade / 3);
    v.thin.frequency.cancelScheduledValues(now); v.thin.frequency.setValueAtTime(20, now); v.thin.frequency.setValueAtTime(20, T); exp(v.thin.frequency, P.thinTo, T + P.fade * 1.5);
    const susEnd = T + P.fade * 2.6;
    b.stop(susEnd, v.susFrom, v.susTo);
    v.crackle.grains(v.crackle.until, T, R.sustain.crackle.rate, R.sustain.crackle.rate, R.sustain.crackle.level);
    v.endAt = T;
    // tail: a thin high whisp rising away, a low sigh, a few last crackles
    const from = b.srcs.length, out = b.gain(1), wet = b.gain(0.6), D = P.dur;
    out.connect(v.out); out.connect(wet).connect(this.send);
    {
      const n = b.noise('white', T), f = b.filter('bandpass', P.whisp.f0 * v.k, P.whisp.q), g = b.gain(0);
      f.frequency.setValueAtTime(P.whisp.f0 * v.k, T); exp(f.frequency, P.whisp.f1 * v.k, T + D);
      g.gain.setValueAtTime(1e-4, T); exp(g.gain, NZ * P.whisp.level, T + 0.06); exp(g.gain, 1e-4, T + D);
      n.connect(f).connect(g).connect(out);
    }
    const sigh = b.noise('brown', T);
    {
      const f = b.filter('lowpass', P.sigh.lp, 0.8), g = b.gain(0);
      f.frequency.setValueAtTime(P.sigh.lp, T); exp(f.frequency, P.sigh.lp * 0.4, T + D * 0.8);
      g.gain.setValueAtTime(1e-4, T); exp(g.gain, NZ * P.sigh.level, T + 0.08); exp(g.gain, 1e-4, T + D * 0.8);
      sigh.connect(f).connect(g).connect(out);
    }
    const cr = new Crackle(b, out, T, R.sustain.crackle.fLo, R.sustain.crackle.fHi, R.sustain.crackle.decay * 1.4);
    cr.grains(T + 0.03, T + D * 0.85, P.crackles / (D * 0.8), P.crackles / (D * 2), P.crackleLevel);
    const last = Math.max(T + D, susEnd) + 0.05;
    b.stop(last, from);
    v.tailFrom = from;
    sigh.onended = () => this.free(v);
  }

  /** hard cut (voice cap): a 10 ms fade, everything stopped */
  private kill(v: Voice, now: number): void {
    v.out.gain.cancelScheduledValues(now); v.out.gain.setValueAtTime(v.r.fireOut, now); v.out.gain.setTargetAtTime(0, now, 0.01);
    v.b.stop(now + 0.06);
    const s = v.b.srcs[v.b.srcs.length - 1]; s.onended = () => this.free(v);
    this.voices.splice(this.voices.indexOf(v), 1);
  }
  private free(v: Voice): void {
    if (v.dead) return; v.dead = true; v.b.free();
    const i = this.voices.indexOf(v); if (i >= 0) this.voices.splice(i, 1);
  }
  private cutCharge(now: number): void {
    const cv = this.charging; if (!cv || cv.dead) return;
    if (now < cv.end - 0.03) { cv.hold.gain.cancelScheduledValues(now); cv.hold.gain.setValueAtTime(1, now); cv.hold.gain.setTargetAtTime(0, now, 0.01); cv.b.stop(now + 0.06); }
    this.charging = null;
  }
}
